import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useAgentStore } from '@/store/agent-store';
import { authFetch, streamChat, cancelSessionGeneration, type StreamGenConfig } from '@/api/request';
import type { Message } from '@/types';
import { stripInternalMarkers } from '@/lib/message-markers';

type ActivityEvent = { type: string; data: Record<string, unknown> };

interface UseAgentChatReturn {
  messages: Message[];
  loading: boolean;
  streaming: boolean;
  messagesLoading: boolean;
  fetchMessages: (sessionId: string) => Promise<any[]>;
  sendMessage: (text: string, sessionId?: string | null, confirm?: boolean, config?: StreamGenConfig, isRetry?: boolean) => Promise<void>;
  clearMessages: () => void;
  abort: (sessionId?: string | null) => void;
}

export function useAgentChat(): UseAgentChatReturn {
  const {
    messages, loading, streaming, messagesLoading, clearMessages, fetchMessages,
  } = useAgentStore();
  const abortRef = useRef<AbortController | null>(null);
  const streamSessionRef = useRef<string | null>(null);
  // BUG-20260812-080: 微调轮次标识（后端 startState.startedAt）——新轮次首个事件到达时
  // 先清空上一轮残留状态再写入，防止新卡片短暂闪现上一轮版本/进度
  const refineRoundRef = useRef<string | undefined>(undefined);

  /** 通过 getState() 获取最新 store 方法，避免 useCallback 闭包陈旧 */
  const store = () => useAgentStore.getState();

  const appendActivity = (messageId: string, event: ActivityEvent) => {
    const current = store().messages.find((m) => m.id === messageId)?.metadata?.activity;
    const activity = Array.isArray(current) ? current : [];
    store().updateMessageMetadata(messageId, {
      activity: [...activity, event].slice(-80),
    });
  };

  const sendMessage = useCallback(
    async (text: string, sessionId?: string | null, confirm = false, config?: StreamGenConfig, isRetry = false) => {
      if (abortRef.current) abortRef.current.abort();

      const controller = new AbortController();
      abortRef.current = controller;
      streamSessionRef.current = sessionId ?? null;

      flushSync(() => {
        store().setLoading(true);
        store().setStreaming(true, sessionId);
        store().setGddConfirmed(false);
        // BUG-20260812-019⑤: 发送指令即预重置微调终态面板——BUG-20260812-080 的重置要等首个
        // refine:stage 事件（意图分类 LLM 有数秒延迟），空窗期流水线卡片仍闪上一轮 done/failed 结果。
        if (store().refineState.status === 'done' || store().refineState.status === 'failed') {
          refineRoundRef.current = undefined;
          store().setRefineState({
            // BUG-20260814-033: thoughts 必须显式清空——setRefineState 是 merge 语义，
            // 不带 thoughts 键会残留上一轮思考流，新轮卡片开头闪旧内容（用户截图实证）
            status: 'idle', stage: undefined, baseVersion: undefined, basePreviewUrl: undefined,
            targetVersion: undefined, newPreviewUrl: undefined, playableId: undefined,
            error: undefined, nodeOutputs: undefined, thoughts: [],
          });
        }
      });

      if (!confirm && !isRetry) {
        const attachMeta: Record<string, unknown> = {};
        if (config?.uploadedImages?.length) attachMeta.uploadedImages = config.uploadedImages;
        if (config?.uploadedVideos?.length) attachMeta.uploadedVideos = config.uploadedVideos;
        store().appendMessage({
          id: `user-${Date.now()}`,
          sessionId: sessionId ?? '',
          role: 'user',
          // 内部技术标记（如 [NO_EARN_FX]）仅供 LLM 消费，不应展示给用户
          content: stripInternalMarkers(text),
          // 随消息透传已上传的图片/视频（文件名等），聊天记录展示缩略图（用户要求发送后可见）
          metadata: Object.keys(attachMeta).length ? attachMeta : undefined,
          createdAt: new Date().toISOString(),
        });
      }

      let assistantMsgId: string | null = null;
      let fullContent = '';

      // 给 React 一个渲染周期显示 loading，再启动 SSE
      await new Promise(r => setTimeout(r, 50));

      try {
        const events = streamChat(text, sessionId, controller.signal, confirm, config);

        for await (const event of events) {
          if (event.type === 'token') {
            const token = typeof event.data === 'string'
              ? event.data
              : ((event.data as { token?: string })?.token ?? '');
            if (!token) continue;
            fullContent += token;

            if (!assistantMsgId) {
              flushSync(() => store().setLoading(false));
              assistantMsgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              store().appendMessage({
                id: assistantMsgId,
                sessionId: sessionId ?? '',
                role: 'assistant',
                content: fullContent,
                metadata: { streaming: true },
                createdAt: new Date().toISOString(),
              });
            } else {
              store().updateLastMessage(fullContent);
            }
          } else if (event.type === 'gdd') {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            const spec = data.spec ?? null;
            const confirmed = Boolean(data.confirmed);
            store().setGddSpec(spec);
            // gdd 是独立 SSE 事件，不能只写全局 store：流式 assistant 文本可能被截断/格式化，
            // ChatMessage 仅从 content 解析时会出现「文档闪现后消失」或完全不出卡片。
            // 将服务端已校验的 spec 固定挂到当前消息，作为稳定渲染源，并兼容无 token 的 GDD 事件。
            if (assistantMsgId) {
              store().updateMessageMetadata(assistantMsgId, {
                gddSpec: spec,
                gddEvent: true,
                // 确认流会产生一条新的 GDD 事件；确认状态必须跟随这条消息，
                // 否则最新卡片会重新显示「确认并生成」按钮。
                ...(confirmed ? { gddConfirmed: true } : {}),
              });
            } else if (spec) {
              assistantMsgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              store().appendMessage({
                id: assistantMsgId,
                sessionId: sessionId ?? '',
                role: 'assistant',
                content: '',
                metadata: {
                  streaming: true,
                  gddSpec: spec,
                  gddEvent: true,
                  ...(confirmed ? { gddConfirmed: true } : {}),
                },
                createdAt: new Date().toISOString(),
              });
              store().setLoading(false);
            }
            if (data.confirmed) {
              store().setGddConfirmed(true);
              // 确认后立即消除 loading + streaming——无 token 事件，必须在此关闭
              store().setLoading(false);
              store().setStreaming(false);
            }
            // GDD 到达/确认都会触发流水线变化（确认后首段 planning 开跑），通知流水线刷新
            store().bumpPipelineTick();
          } else if (event.type === 'pipeline:stage' || event.type === 'pipeline:paused') {
            // 分段流水线节点事件：轮询未启动（无 running 节点）时前端无感知，必须事件驱动刷新
            store().bumpPipelineTick();
          } else if (event.type === 'refine:thought') {
            // BUG-20260813-029: 微调思考过程实时流——首条 thought 先于 refine:stage 到达
            // （understand 进节点即上报），此时 status!=='running' 判为新轮：重置 thoughts 并置 running。
            // startedAt 重置 patch 不含 thoughts 键，不会抹掉本轮首批思考。
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            const prev = store().refineState;
            const fresh = prev.status !== 'running';
            if (!assistantMsgId) {
              flushSync(() => store().setLoading(false));
              assistantMsgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              store().appendMessage({
                id: assistantMsgId,
                sessionId: sessionId ?? '',
                role: 'assistant',
                content: '',
                metadata: { streaming: true, isRefinement: true },
                createdAt: new Date().toISOString(),
              });
            } else {
              store().updateMessageMetadata(assistantMsgId, { isRefinement: true });
            }
            store().setRefineState({
              status: 'running',
              ...(fresh ? { error: undefined } : {}),
              thoughts: [...(fresh ? [] : (prev.thoughts ?? [])), data.text as string].slice(-40),
            });
          } else if (event.type === 'refine:stage' || event.type === 'refine:preview' || event.type === 'refine:done' || event.type === 'refine:failed') {
            // 独立微调图事件：写 refineState（与 pipelineTick 隔离），preview/done 时请求预览切换
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            const isPreview = event.type === 'refine:preview';
            const isDone = event.type === 'refine:done';
            const isFailed = event.type === 'refine:failed';
            const prev = store().refineState;
            // BUG-20260812-080: 新一轮（startedAt 变化）先初始化微调状态——上一轮的
            // nodeOutputs/base/target/playableId 全部清掉，卡片不再闪现上一轮内容；
            // 用 startedAt 而非 stage 判新轮，verify 失败重试回 understand 不误触发重置
            if (data.startedAt && refineRoundRef.current !== data.startedAt) {
              refineRoundRef.current = data.startedAt as string;
              store().setRefineState({
                // BUG-20260814-033: 新轮重置必须清 thoughts（merge 语义不带键不清）
                status: 'idle', stage: undefined, baseVersion: undefined, basePreviewUrl: undefined,
                targetVersion: undefined, newPreviewUrl: undefined, playableId: undefined,
                error: undefined, nodeOutputs: undefined, thoughts: [],
              });
            }
            store().setRefineState({
              status: isDone ? 'done' : isFailed ? 'failed' : 'running',
              stage: data.stage ?? prev.stage,
              baseVersion: data.baseVersion ?? prev.baseVersion,
              basePreviewUrl: data.basePreviewUrl ?? prev.basePreviewUrl,
              // 新一轮残留已在上方 startedAt 重置中清掉，这里仅透传本轮事件字段
              targetVersion: data.targetVersion,
              newPreviewUrl: data.newPreviewUrl,
              playableId: data.playableId,
              error: data.error,
              nodeOutputs: data.nodeOutputs ?? prev.nodeOutputs,
            });
            // 微调卡片绑定到当前 assistant 消息（刷新历史后仍可识别）
            if (assistantMsgId) {
              const base = { isRefinement: true };
              if (isDone) {
                store().updateMessageMetadata(assistantMsgId, {
                  ...base,
                  // BUG-20260813-030: thoughts 随 refineResult 持久化——刷新/非 live 卡片可回看思考轨迹
                  refineResult: { ok: true, baseVersion: data.baseVersion, basePreviewUrl: store().refineState.basePreviewUrl, targetVersion: data.targetVersion, newPreviewUrl: data.newPreviewUrl, thoughts: data.thoughts },
                });
              } else if (isFailed) {
                store().updateMessageMetadata(assistantMsgId, {
                  ...base,
                  // BUG-20260821-PW-001（评审加固）: stage 一并存入 refineResult——刷新后历史卡片据此标真实失败阶段
                  refineResult: { ok: false, baseVersion: data.baseVersion, basePreviewUrl: store().refineState.basePreviewUrl, error: data.error, stage: data.stage, thoughts: data.thoughts },
                });
              } else {
                store().updateMessageMetadata(assistantMsgId, base);
              }
            }
            // preview 事件：立即显示临时预览；done 事件：用户 Accept 后的最终预览
            if ((isPreview || isDone) && data.newPreviewUrl) {
              store().requestPreview(data.newPreviewUrl as string);
            }
          } else if (event.type === 'thinking' || event.type === 'search' || event.type === 'action') {
            // 思考、搜索、操作事件追加到活动时间线，不再覆盖上一条事件。
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            const activityEvent = { type: event.type, data: data as Record<string, unknown> };
            if (!assistantMsgId) {
              flushSync(() => store().setLoading(false));
              assistantMsgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              store().appendMessage({
                id: assistantMsgId,
                sessionId: sessionId ?? '',
                role: 'assistant',
                content: '',
                metadata: {
                  streaming: true,
                  [event.type]: data,
                  activity: [activityEvent],
                },
                createdAt: new Date().toISOString(),
              });
            } else {
              store().updateMessageMetadata(assistantMsgId, { [event.type]: data });
              appendActivity(assistantMsgId, activityEvent);
            }
          } else if (event.type === 'duplicate_confirm') {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            store().setLoading(false);
            store().appendMessage({
              id: `dup-${Date.now()}`,
              sessionId: sessionId ?? '',
              role: 'assistant',
              content: '',
              metadata: { duplicateConfirm: data },
              createdAt: new Date().toISOString(),
            });
          } else if (event.type === 'video_material_confirm') {
            // 素材确认卡片数据：存储到 assistantMsgId 对应的消息 metadata，确保 SSE 期间卡片立即渲染
            if (assistantMsgId) {
              store().updateMessageMetadata(assistantMsgId, { videoMaterialConfirm: event.data });
            }
          } else if (event.type === 'video_plan_confirm') {
            // 方案确认卡片数据（FEAT-20260907-VIDEO-PLAN）：按视频段落展示方案，5s 倒计时
            if (assistantMsgId) {
              store().updateMessageMetadata(assistantMsgId, { videoPlanConfirm: event.data });
            }
          } else if (event.type === 'tool_call') {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            const activityEvent = { type: event.type, data: data as Record<string, unknown> };
            if (!assistantMsgId) {
              flushSync(() => store().setLoading(false));
              assistantMsgId = `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              store().appendMessage({
                id: assistantMsgId,
                sessionId: sessionId ?? '',
                role: 'assistant',
                content: '',
                metadata: { streaming: true, activity: [activityEvent] },
                createdAt: new Date().toISOString(),
              });
            } else {
              appendActivity(assistantMsgId, activityEvent);
            }
          } else if (event.type === 'done') {
            break;
          } else if (event.type === 'error') {
            throw new Error(typeof event.data === 'string' ? event.data : 'Unknown error');
          }
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        store().appendMessage({
          id: `error-${Date.now()}`,
          sessionId: sessionId ?? '',
          role: 'assistant',
          content: '请求失败，请重试',
          metadata: { error: err instanceof Error ? err.message : 'Unknown error' },
          createdAt: new Date().toISOString(),
        });
      } finally {
        const isLatest = abortRef.current === controller;
        // flushSync 强制同步渲染，确保 streaming 态立即切换为 markdown
        flushSync(() => {
          store().finishLastMessage();
          store().setLoading(false);
          store().setStreaming(false);
        });
        // DO-NOT-REVERT: BUG-20260909-PW-003 SSE 流结束但 refineState 仍 running 时，先向后端核对
        // refine 真实状态再决定终态——流可能在 done/failed 事件到达前断开，而后端任务仍在跑/已完成；
        // 一律翻 failed 会让用户在微调实际成功时看到「连接中断」并误重试（重复微调/重开流水线）。
        // 原 fail-fast（BUG-20260813-023）仅在后端无 refine 记录/查询失败时兜底。
        if (store().refineState.status === 'running' && sessionId) {
          void (async () => {
            const prev = store().refineState;
            const dropErr = '连接中断，意图优化未完成，请重试';
            let backendStatus: string | undefined;
            let backendRefine: Record<string, unknown> | undefined;
            try {
              const res = await authFetch(`/api/agent/session/${sessionId}/nodes`);
              const body = await res.json();
              backendRefine = (body?.data?.refine ?? body?.refine) as Record<string, unknown> | undefined;
              backendStatus = backendRefine?.status as string | undefined;
            } catch { /* 查询失败走 fail-fast 兜底 */ }
            // 写入前会话守卫：查询期间用户可能已切会话/主动停止（refineState 被重置）→ 丢弃本次收尾
            if (store().refineState.status !== 'running') return;
            if (backendStatus === 'done' || backendStatus === 'preview') {
              // 后端已完成：按真实终态收尾（preview 映射 done），并请求预览切换 + 拉已持久化消息
              const newUrl = typeof backendRefine?.newPreviewUrl === 'string' ? backendRefine.newPreviewUrl : undefined;
              store().setRefineState({
                ...prev,
                status: 'done',
                stage: backendRefine?.stage as 'understand' | 'apply' | 'verify' | undefined,
                targetVersion: backendRefine?.targetVersion as number | undefined,
                newPreviewUrl: newUrl,
                playableId: backendRefine?.playableId as string | undefined,
                error: undefined,
              });
              if (assistantMsgId) {
                store().updateMessageMetadata(assistantMsgId, {
                  isRefinement: true,
                  refineResult: { ok: true, baseVersion: prev.baseVersion, basePreviewUrl: prev.basePreviewUrl, targetVersion: backendRefine?.targetVersion, newPreviewUrl: newUrl, thoughts: prev.thoughts },
                });
              }
              if (newUrl) store().requestPreview(newUrl);
              // BUG-20260813-030 语义：完成后拉后端已持久化消息，历史卡片拿到完整 refineResult
              void fetchMessages(sessionId);
            } else if (backendStatus === 'failed') {
              const backendErr = backendRefine?.error as string | undefined;
              store().setRefineState({ ...prev, status: 'failed', error: backendErr || dropErr });
              if (assistantMsgId) {
                store().updateMessageMetadata(assistantMsgId, {
                  isRefinement: true,
                  refineResult: { ok: false, baseVersion: prev.baseVersion, basePreviewUrl: prev.basePreviewUrl, error: backendErr || dropErr },
                });
              }
            } else if (backendStatus === 'running') {
              // 后端任务仍在执行：保持 running，让 pipelineTick 驱动的轮询（use-pipeline-poll）
              // 持续同步 refine 状态直至终态——不误报失败，用户可用「停止」逃生
              store().bumpPipelineTick();
            } else {
              // 后端无 refine 记录/查询失败 → 原 fail-fast 兜底（BUG-20260813-023）
              store().setRefineState({ ...prev, status: 'failed', error: dropErr });
              if (assistantMsgId) {
                store().updateMessageMetadata(assistantMsgId, {
                  isRefinement: true,
                  refineResult: { ok: false, baseVersion: prev.baseVersion, basePreviewUrl: prev.basePreviewUrl, error: dropErr },
                });
              }
            }
          })();
        }
        if (isLatest) {
          abortRef.current = null;
          streamSessionRef.current = null;
        }
      }
    },
    [], // 无依赖：所有 store 操作通过 getState() 获取最新引用
  );

  const abort = useCallback((sessionId?: string | null) => {
    const sid = sessionId ?? streamSessionRef.current;
    abortRef.current?.abort();
    if (sid) cancelSessionGeneration(sid).catch(() => {});
  }, []);

  return { messages, loading, streaming, messagesLoading, fetchMessages, sendMessage, clearMessages, abort };
}
